<script>
  import { mapGetters } from 'vuex';
  import { ZonePlanLayer } from '../viewer/zonePlanOverlay';
  import { error } from '@/utils/debugLogger';

  const Autodesk = window.Autodesk;

  /**
   * Renderless: keeps zone boundaries drawn on the open 2D level plan while the quick-menu
   * Zones toggle is on (AVA-765). Follows plan/level switches, the palette's type filter and
   * the selected zone. Mounted once in the viewer layout.
   */
  export default {
    name: 'ZonesPlanLayer',
    computed: {
      ...mapGetters('ZONES', {
        show: 'GET_SHOW_PLAN_BOUNDARIES',
        zones: 'GET_FILTERED_ZONES',
        selectedZoneId: 'GET_SELECTED_ZONE_ID',
      }),
      planViewer() {
        return this.$store.state.planViewer;
      },
      level() {
        return this.$store.state.selectedLevel;
      },
      facilityId() {
        return this.$store.state.assetId;
      },
      layerKey() {
        return [this.show, this.zones.map((z) => `${z.id}:${z.colour}:${z.name}`).join(), this.selectedZoneId, this.level?.id].join('|');
      },
    },
    watch: {
      layerKey: 'sync',
      planViewer: 'sync',
      show: 'ensureZones',
      facilityId: 'ensureZones',
    },
    created() {
      this.layer = null; // ZonePlanLayer; not reactive
      this.waitingViewer = null;
    },
    mounted() {
      this.sync();
    },
    beforeDestroy() {
      this.dropLayer();
    },
    methods: {
      // The palette may never have opened: load the facility's zones when the toggle needs them.
      ensureZones() {
        if (!this.show) return;
        this.$store.dispatch('ZONES/ENSURE_LOADED').catch((err) => error('Failed to load zones', err));
      },
      sync() {
        const viewer = this.planViewer;
        if (!this.show || !viewer) {
          this.dropLayer();
          return;
        }
        if (this.layer && this.layer.viewer !== viewer) this.dropLayer();
        // The plan viewer is published before its sheet loads; draw once geometry is in.
        if (!viewer.model || !viewer.model.isLoadDone?.()) {
          this.waitForGeometry(viewer);
          return;
        }
        if (!this.layer) this.layer = new ZonePlanLayer(viewer);
        this.layer.render(this.zones, this.level, this.selectedZoneId);
      },
      waitForGeometry(viewer) {
        if (this.waitingViewer === viewer) return;
        this.waitingViewer = viewer;
        const onLoaded = () => {
          viewer.removeEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, onLoaded);
          if (this.waitingViewer === viewer) this.waitingViewer = null;
          this.sync();
        };
        viewer.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, onLoaded);
      },
      dropLayer() {
        try {
          this.layer?.destroy();
        } catch (err) {
          // The plan viewer may already be torn down on a level switch.
          error('Failed to clear zone plan overlay', err);
        }
        this.layer = null;
      },
    },
    render() {
      return null;
    },
  };
</script>
